import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { BeastieComponent, ElementType } from '../components/BeastieComponent';
import { AIComponent } from '../components/AIComponent';
import { TamingComponent } from '../components/TamingComponent';
import { EventBus } from '../../utils/EventBus';

/**
 * Taming item configuration
 */
export interface TamingItem {
  id: string;
  name: string;
  baseLoyaltyGain: number;
  preferredElements?: ElementType[];
}

/**
 * TamingSystem - Handles taming/capturing wild beasties
 * Uses loyalty/feeding mechanic where player offers items to befriend
 */
export class TamingSystem extends System {
  public readonly requiredComponents = ['beastie', 'ai', 'taming'];

  /** Maximum distance for taming interactions */
  private interactionRange = 2.5;

  /** Maximum party size */
  private maxPartySize = 6;

  /** Available taming items */
  private tamingItems: Map<string, TamingItem> = new Map([
    ['berry', { id: 'berry', name: 'Wild Berry', baseLoyaltyGain: 10 }],
    ['treat', { id: 'treat', name: 'Beastie Treat', baseLoyaltyGain: 18 }],
    [
      'favorite_food',
      { id: 'favorite_food', name: 'Favorite Food', baseLoyaltyGain: 30, preferredElements: [] },
    ],
  ]);

  /** Currently selected target for taming */
  private selectedTarget: string | null = null;

  public init(): void {
    // Listen for player feed commands
    EventBus.onEvent('player:command', (data) => {
      if (data.command === 'feed_beastie') {
        const feedData = data.data as { targetId: string; itemId: string };
        this.handleFeedCommand(feedData.targetId, feedData.itemId);
      } else if (data.command === 'capture_beastie') {
        const captureData = data.data as { targetId: string };
        this.handleCaptureCommand(captureData.targetId);
      } else if (data.command === 'select_taming_target') {
        const selectData = data.data as { targetId: string };
        this.selectTarget(selectData.targetId);
      }
    });
  }

  public update(entities: Entity[], delta: number): void {
    const deltaSeconds = delta / 1000;
    const currentTime = Date.now();

    for (const entity of entities) {
      const taming = entity.getComponent<TamingComponent>('taming');
      const ai = entity.getComponent<AIComponent>('ai');
      const beastie = entity.getComponent<BeastieComponent>('beastie');

      if (!taming || !ai || !beastie) continue;

      // Only process wild beasties being tamed
      if (!beastie.isWild || !taming.isBeingTamed) continue;

      // Apply loyalty decay if not recently interacted (after 5 seconds of no interaction)
      if (currentTime - taming.lastInteractionTime > 5000) {
        taming.applyDecay(deltaSeconds);

        // If loyalty drops to 0, end taming and flee
        if (taming.tamingProgress <= 0) {
          this.endTaming(entity, 'lost_interest');
        }
      }

      // Process taming state behavior
      if (ai.state === 'taming') {
        this.processTamingBehavior(entity);
      }
    }
  }

  /**
   * Select a target for taming
   */
  private selectTarget(targetId: string): void {
    const target = this.world.getEntity(targetId);
    if (!target) return;

    const beastie = target.getComponent<BeastieComponent>('beastie');
    if (!beastie || !beastie.isWild) {
      EventBus.emitEvent('ui:notification', {
        message: 'Cannot tame: not a wild beastie',
        type: 'warning',
      });
      return;
    }

    this.selectedTarget = targetId;
  }

  /**
   * Handle player feed command
   */
  private handleFeedCommand(targetId: string, itemId: string): void {
    const target = this.world.getEntity(targetId);
    const player = this.world.getEntity('player');

    if (!target || !player) {
      EventBus.emitEvent('ui:notification', { message: 'Invalid target', type: 'error' });
      return;
    }

    const beastie = target.getComponent<BeastieComponent>('beastie');
    const ai = target.getComponent<AIComponent>('ai');

    if (!beastie || !ai || !beastie.isWild) {
      EventBus.emitEvent('ui:notification', {
        message: 'Cannot tame: not a wild beastie',
        type: 'warning',
      });
      return;
    }

    // Check distance
    const playerTransform = player.getComponent<TransformComponent>('transform');
    const targetTransform = target.getComponent<TransformComponent>('transform');

    if (!playerTransform || !targetTransform) return;

    const distance = Math.sqrt(
      Math.pow(targetTransform.x - playerTransform.x, 2) +
        Math.pow(targetTransform.y - playerTransform.y, 2)
    );

    if (distance > this.interactionRange) {
      EventBus.emitEvent('ui:notification', { message: 'Too far away!', type: 'warning' });
      return;
    }

    // Ensure taming component exists
    let taming = target.getComponent<TamingComponent>('taming');
    if (!taming) {
      taming = new TamingComponent({
        captureThreshold: this.calculateCaptureThreshold(beastie),
      });
      target.addComponent(taming);
    }

    // Check cooldown
    const currentTime = Date.now();
    if (!taming.canFeed(currentTime)) {
      const waitTime = Math.ceil(taming.getRemainingCooldown(currentTime) / 1000);
      EventBus.emitEvent('ui:notification', {
        message: `Wait ${waitTime}s to feed again`,
        type: 'info',
      });
      return;
    }

    // Get item and calculate loyalty gain
    const item = this.tamingItems.get(itemId);
    if (!item) {
      EventBus.emitEvent('ui:notification', { message: 'Invalid item', type: 'error' });
      return;
    }

    // Calculate loyalty gain with bonuses
    let loyaltyGain = item.baseLoyaltyGain;

    // Element match bonus
    if (item.preferredElements?.some((e) => beastie.elementTypes.includes(e))) {
      loyaltyGain = Math.floor(loyaltyGain * 1.5);
    }

    // Personality modifier
    if (beastie.personality === 'cautious') {
      loyaltyGain = Math.floor(loyaltyGain * 0.8);
    } else if (beastie.personality === 'bold') {
      loyaltyGain = Math.floor(loyaltyGain * 1.2);
    }

    // Start taming if not already
    if (!taming.isBeingTamed) {
      taming.isBeingTamed = true;
      taming.tamerId = 'player';
      ai.setState('taming');

      EventBus.emitEvent('taming:started', {
        beastieId: target.id,
        playerId: 'player',
      });
    }

    // Apply loyalty
    taming.addLoyalty(loyaltyGain, itemId, currentTime);

    // Also update beastie's base loyalty for when captured
    beastie.loyalty = Math.min(100, beastie.loyalty + loyaltyGain / 2);

    console.log(
      `Fed ${beastie.speciesId} with ${item.name}: +${loyaltyGain} loyalty (${taming.tamingProgress.toFixed(0)}/${taming.captureThreshold})`
    );

    // Emit events
    EventBus.emitEvent('taming:fed', {
      beastieId: target.id,
      itemId: itemId,
      loyaltyGain: loyaltyGain,
      newProgress: taming.tamingProgress,
      threshold: taming.captureThreshold,
    });

    EventBus.emitEvent('ui:notification', {
      message: `+${loyaltyGain} friendship!`,
      type: 'info',
    });

    // Check if can capture now
    if (taming.canCapture()) {
      EventBus.emitEvent('ui:notification', {
        message: `${beastie.speciesId} is ready to join you!`,
        type: 'info',
      });
    }
  }

  /**
   * Handle capture command
   */
  private handleCaptureCommand(targetId: string): void {
    const target = this.world.getEntity(targetId);
    const player = this.world.getEntity('player');

    if (!target || !player) {
      EventBus.emitEvent('ui:notification', { message: 'Invalid target', type: 'error' });
      return;
    }

    const beastie = target.getComponent<BeastieComponent>('beastie');
    const taming = target.getComponent<TamingComponent>('taming');

    if (!beastie || !taming) {
      EventBus.emitEvent('ui:notification', { message: 'Feed it first!', type: 'warning' });
      return;
    }

    // Check distance
    const playerTransform = player.getComponent<TransformComponent>('transform');
    const targetTransform = target.getComponent<TransformComponent>('transform');

    if (!playerTransform || !targetTransform) return;

    const distance = Math.sqrt(
      Math.pow(targetTransform.x - playerTransform.x, 2) +
        Math.pow(targetTransform.y - playerTransform.y, 2)
    );

    if (distance > this.interactionRange) {
      EventBus.emitEvent('ui:notification', { message: 'Too far away!', type: 'warning' });
      return;
    }

    if (!taming.canCapture()) {
      const remaining = Math.ceil(taming.captureThreshold - taming.tamingProgress);
      EventBus.emitEvent('ui:notification', {
        message: `Need ${remaining} more friendship`,
        type: 'warning',
      });
      return;
    }

    // Check party space
    const partyMembers = this.world.getEntitiesByTag('party_member');
    const beastiePartyMembers = partyMembers.filter((e) => e.hasComponent('beastie'));

    if (beastiePartyMembers.length >= this.maxPartySize) {
      EventBus.emitEvent('ui:notification', {
        message: 'Party is full!',
        type: 'error',
      });
      return;
    }

    // Capture success!
    this.captureBeastie(target, beastie);
  }

  /**
   * Convert wild beastie to party member
   */
  private captureBeastie(entity: Entity, beastie: BeastieComponent): void {
    const ai = entity.getComponent<AIComponent>('ai');

    // Update beastie state
    beastie.isWild = false;
    beastie.ownerId = 'player';
    beastie.loyalty = 50; // Start with decent loyalty

    // Update tags
    entity.removeTag('wild');
    entity.removeTag('enemy');
    entity.addTag('party_member');
    // Ensure beastie tag is present (should already be there)
    if (!entity.hasTag('beastie')) {
      entity.addTag('beastie');
    }

    // Update AI to follow player
    if (ai) {
      ai.setState('following');
      ai.ownerEntityId = 'player';
      ai.isActive = true;
      ai.targetId = null;
    }

    // Remove taming component
    entity.removeComponent('taming');

    // Clear selected target
    if (this.selectedTarget === entity.id) {
      this.selectedTarget = null;
    }

    // Emit capture event
    EventBus.emitEvent('taming:captured', {
      beastieId: entity.id,
      speciesId: beastie.speciesId,
    });

    // Notify wild beastie spawner
    EventBus.emitEvent('beastie:despawned', { id: entity.id });

    console.log(`Captured ${beastie.speciesId}! Welcome to the party.`);

    EventBus.emitEvent('ui:notification', {
      message: `${beastie.speciesId} joined your party!`,
      type: 'info',
    });
  }

  /**
   * Calculate capture threshold based on beastie properties
   */
  private calculateCaptureThreshold(beastie: BeastieComponent): number {
    let threshold = 50;

    // Higher level = harder to catch
    threshold += beastie.level * 2;

    // Personality affects difficulty
    if (beastie.personality === 'aggressive') threshold += 15;
    if (beastie.personality === 'cautious') threshold += 10;
    if (beastie.personality === 'bold') threshold -= 5;

    return Math.min(95, Math.max(40, threshold));
  }

  /**
   * Process behavior while being tamed
   */
  private processTamingBehavior(entity: Entity): void {
    const ai = entity.getComponent<AIComponent>('ai');

    if (!ai) return;

    // Stay mostly still while being tamed
    // The AI system will handle the taming state behavior
  }

  /**
   * End taming session
   */
  private endTaming(entity: Entity, reason: string): void {
    const taming = entity.getComponent<TamingComponent>('taming');
    const ai = entity.getComponent<AIComponent>('ai');
    const beastie = entity.getComponent<BeastieComponent>('beastie');

    if (taming) {
      taming.reset();
    }

    if (ai) {
      ai.setState('fleeing');
      // Will return to patrol after fleeing (handled by AI system)
    }

    // Clear selected target
    if (this.selectedTarget === entity.id) {
      this.selectedTarget = null;
    }

    EventBus.emitEvent('taming:fled', { beastieId: entity.id });

    console.log(`Taming ended for ${beastie?.speciesId}: ${reason}`);
  }

  /**
   * Get the currently selected taming target
   */
  public getSelectedTarget(): string | null {
    return this.selectedTarget;
  }

  public destroy(): void {
    // Event listeners are cleaned up by EventBus.removeAllListeners in GameScene
  }
}
